// DayZ-style inventory screen (Tab) + always-on survival HUD.
//
// Screen layout (three anchored full-height columns, DayZ 1.x):
//   VICINITY (ground loot within reach) | CHARACTER (hands + paperdoll +
//   worn garments) | CONTAINERS (the grids of everything worn).
// Items are icon-first: a vector silhouette per item id (see itemicons.js),
// a quantity chip and a condition bar — never a text node with the name in it.
// Items drag between grids; right-click opens an action menu.
//
// HUD: iconized vitals column (bottom-right), icon-only quickbar (keys
// 8 9 0 -), keycap interact prompt.
import { ITEMS } from '../game/items';
import { injectTheme } from './theme';
import { pokeHud } from './idle';
import { ITEM_ICON_DEFS, iconFor, iconAspect, missingIcons, iconCollisions, WEAPON_ICON } from './itemicons';
import { massOf, massOfStack, massOfItems, kg } from './itemmass';
import { planDrop, planSplit, mergedCond, fitsAt } from './invmove.mjs';

const NS = 'http://www.w3.org/2000/svg';

// <svg class="cls"><use href="#ic-sym"/></svg>, built without innerHTML so
// item names/ids can never be re-parsed as markup.
function icon(sym, cls, tint) {
  const s = document.createElementNS(NS, 'svg');
  s.setAttribute('class', cls);
  if (tint) s.style.color = tint;
  const u = document.createElementNS(NS, 'use');
  u.setAttribute('href', '#ic-' + sym);
  s.appendChild(u);
  return s;
}

const el = (tag, cls, text) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};

// ---------------------------------------------------------------------------
// item condition: DayZ's core inventory signal. Lives on the item INSTANCE as
// `cond` (0..1); absent means factory-fresh. The ramp is shared with the
// vitals column so the whole UI speaks one color language.
// ---------------------------------------------------------------------------
// PRISTINE DRAWS NOTHING. Round 4 gated the bar on c < 0.999 with a
// var(--hud-text) fill, so a 0.95-condition bandage painted a 100%-white bar
// across its slot — the brightest pixel cluster in the whole HUD. DayZ shows no
// indicator until an item has actually left Pristine; COND_BAR is that gate and
// the Pristine swatch is a dim neutral that can never lead the eye.
// SEVERITY MUST GAIN PRESENCE, NOT LOSE IT. Round 5 painted the bar's width
// from the REMAINING condition, so a Ruined item drew a 10% stub of the darkest
// colour on the ramp while a merely-Worn one drew a 90% bar of the brightest —
// the catastrophic state was the quietest slot on screen. Three changes fix it
// and they are all monotonic in severity:
//   * the bar is a DAMAGE bar: its fill is (1 - condition), so ink area grows
//     with the damage instead of shrinking away to nothing;
//   * alpha ramps .5 -> 1 and the bar thickens by a pixel at the bottom two
//     steps, so a Ruined item physically weighs more on the plate;
//   * chroma ramps monotonically (neutral -> gold -> amber -> brick).
// NB: strictly monotonic *luminance* toward Ruined, as the round-5 critique
// asks for, is not reachable on a ramp that ends in red — Rec709 luma of any
// saturated red tops out near 115, below every gold and amber (the critique's
// own proposed swatches land at Worn 114 / Damaged 141 / Badly 128 / Ruined 88,
// i.e. a worse hump than the one it flags). Presence here is carried by ink
// area, alpha and chroma, all three of which ARE monotonic.
const COND = [
  [0.88, 'Pristine', '#7f857c', 0],
  [0.66, 'Worn', '#7c8177', 0.5],
  [0.42, 'Damaged', '#a08a4a', 0.7],
  [0.18, 'Badly Damaged', '#c07434', 0.88],
  [-1, 'Ruined', '#d94434', 1],
];
const COND_BAR = 0.88; // below this the condition bar appears at all

// ---------------------------------------------------------------------------
// QUICKSLOTS. The starter kit is a T-shirt and shorts, and both carry
// `quickslots: 0` in items.js — so Inventory.quickslotCount() returned 0 for
// 70 of the 70 persisted players: the bar rendered as an empty div, the
// context menu offered no "Quickbar" rows, useQuickslot() returned on its
// first line, and the F1 sheet advertised keys 8/9/0/- that did nothing.
//
// THIS IS A CHANGE TO HOW THE BAR IS DERIVED, NOT TO THE STARTER GARMENTS, and
// deliberately so:
//  * Both routes reach the existing 70 players — `quickslots` is read live out
//    of ITEMS and is never persisted (toJSON stores only id/x/y/qty) — so that
//    is not the deciding factor.
//  * Giving a T-shirt with a 1x1 pocket grid a quickslot is a lie about the
//    garment, and it would travel with every T-shirt in the game forever.
//  * Worse, it ties the FLOOR to a garment: strip your shirt, or swap shorts
//    for a better pair that happens to list 0, and the keys the help sheet
//    teaches silently die again. A derived floor cannot regress that way.
// So: every survivor has two quickslots (what a hand can hold and a waistband
// can take), and clothing ADDS to them up to the four the keys cover.
const BASE_QUICKSLOTS = 2;
// the keys input.js binds, in slot order — one source of truth for the bar,
// the context menu and the tooltips
const QUICK_KEYS = ['8', '9', '0', '-'];
// Split halves mint their own uids while src/game/inventory.js has no
// splitStack(); this base keeps them clear of the model's counter (which
// starts at 1 and increments once per created record) forever. uids are
// session-only — toJSON() does not persist them — so this cannot leak into a
// save file. See handoff notes for the model API that retires this.
const VIEW_UID_BASE = 1e9;
let viewUid = VIEW_UID_BASE;
// how much of an item's plate the silhouette occupies (DayZ: edge to edge)
const ICON_FILL = 0.9;
// catalog lookups the pure planner needs, passed in as callbacks so
// invmove.mjs stays importable by a Node harness
const SIZE_OF = (id) => ITEMS[id].size;
const STACK_OF = (id) => ITEMS[id].stack || 1;

export const condOf = (it) => (typeof it.cond === 'number' ? Math.max(0, Math.min(1, it.cond)) : 1);
export const condStep = (c) => COND.find((s) => c > s[0]) || COND[COND.length - 1];

// the damage bar itself — one builder, used by the grid plate and the quickbar
// so the two can never drift apart. Returns null while the item is Pristine.
function condBarEl(c) {
  if (c >= COND_BAR) return null;
  const step = condStep(c);
  const bar = el('span', 'cbar' + (c <= 0.42 ? ' heavy' : ''));
  const fill = el('i');
  fill.style.width = ((1 - c) * 100).toFixed(0) + '%';
  fill.style.background = step[2];
  fill.style.opacity = step[3];
  bar.appendChild(fill);
  return bar;
}

// 22px inline-SVG vitals icons (DayZ iconized column, bottom-right).
// fill:currentColor so value maps to icon color, no labels, no numbers.
// All symbols are normalized to the SAME optical bounding box (~x 4-20 of the
// 24px viewBox) so the flex column's gap reads even, not ragged.
const VITAL_DEFS = `
<svg style="display:none" xmlns="http://www.w3.org/2000/svg"><defs>
  <symbol id="vi-heart" viewBox="0 0 24 24"><path fill="currentColor"
    d="M12 20.8C10 19.2 4 14.6 4 10.2 4 7.4 6.1 5.5 8.4 5.5c1.5 0 2.8.8 3.6 2.1.8-1.3 2.1-2.1 3.6-2.1 2.3 0 4.4 1.9 4.4 4.7 0 4.4-6 9-8 10.6z"/></symbol>
  <symbol id="vi-food" viewBox="0 0 24 24"><g transform="matrix(1.16 0 0 1 -2.3 0)"><path fill="currentColor"
    d="M5.5 2h1.6v5.2h1V2h1.6v5.2h1V2h1.6v6.4c0 1.4-.9 2.5-2.2 2.9V22H7.7V11.3c-1.3-.4-2.2-1.5-2.2-2.9V2z"/>
    <path fill="currentColor"
    d="M17.9 2c-2 2.6-3 5.9-3 8.9 0 1.6.9 2.8 2.1 3.2V22h2.1V2h-1.2z"/></g></symbol>
  <symbol id="vi-drop" viewBox="0 0 24 24"><path fill="currentColor"
    d="M12 2.6s7.6 8.7 7.6 14a7.6 7.6 0 0 1-15.2 0c0-5.3 7.6-14 7.6-14z"/></symbol>
  <!-- BLOOD is a blood bag, NOT a droplet: at 23px a droplet-plus-inset-cross
       filled in and became indistinguishable from THIRST, which is the
       difference between drinking and bleeding out. These two now differ by
       OUTLINE (flat-topped pouch + drip tube vs. pointed droplet). -->
  <symbol id="vi-blood" viewBox="0 0 24 24"><g fill="currentColor">
    <path d="M9.6 1.6h4.8v1.9H9.6z"/>
    <path d="M6.5 3.9h11c1 0 1.8.8 1.8 1.8v8.9c0 3.3-2.7 6-6 6h-2.6c-3.3 0-6-2.7-6-6V5.7c0-1 .8-1.8 1.8-1.8z"/>
    <path d="M11.1 20.4h1.8v2.9h-1.8z"/></g>
    <rect x="8.1" y="6.1" width="7.8" height="2" rx="1" fill="rgba(0,0,0,.42)"/></symbol>
  <symbol id="vi-bandage" viewBox="0 0 24 24"><g transform="rotate(45 12 12)"><rect
    x="3.5" y="8.5" width="17" height="7" rx="3.5" fill="currentColor"/><rect x="9.5" y="10"
    width="5" height="4" rx="1" fill="rgba(0,0,0,.5)"/></g></symbol>
</defs></svg>`;

// ---------------------------------------------------------------------------
// Paperdoll. Round 4 drew a 10%-alpha grey mannequin that filled 86% of the
// tallest column and carried ZERO information — the largest element on the
// screen was decoration. The silhouette now WEARS what you are wearing: every
// equipped garment paints its own region, clipped to the body, in that item's
// icon tint, so the centre column answers "what am I dressed in" at a glance.
// ---------------------------------------------------------------------------
const BODY_TOP = `M60 4c9 0 15 7.4 15 17.4 0 6.4-2.2 11.6-5.4 14.6 8.6 2.4 16 5 21.6 7.6
   6.6 3 10.4 7 11.6 13.4l6 34c.8 4.4-1.2 7.4-5.4 8.2-4 .8-7-1.2-7.8-5.4l-4.6-22.6-2 40.2 4.6 62.6
   c.6 7.6-3.2 11.8-9.6 11.8-5.4 0-8.8-3-9.8-9.2L60 128l-14.8 48.6c-1 6.2-4.4 9.2-9.8 9.2-6.4 0-10.2-4.2-9.6-11.8
   l4.6-62.6-2-40.2-4.6 22.6c-.8 4.2-3.8 6.2-7.8 5.4-4.2-.8-6.2-3.8-5.4-8.2l6-34c1.2-6.4 5-10.4 11.6-13.4
   5.6-2.6 13-5.2 21.6-7.6C47.2 33 45 27.8 45 21.4 45 11.4 51 4 60 4z`;
// The boots start at y=184, not 190: at 190 they floated four units clear of
// the shin and the figure read as a broken doll rather than a standing body.
const BODY_FEET = `M38 184h16l3 68c.2 4-2.2 6-6.6 6h-9c-4.4 0-6.8-2-6.6-6zM66 184h16l3.2 68
   c.2 4-2.2 6-6.6 6h-9c-4.4 0-6.8-2-6.6-6z`;
// worn region per slot inside the 120x260 doll space (clipped to the body)
const WORN_BAND = {
  shirt: [0, 40, 120, 74],
  jacket: [0, 34, 120, 104],
  pants: [0, 126, 120, 74],
};

function dollSvg(tintOf) {
  const band = (slot) => {
    const t = tintOf(slot);
    if (!t) return '';
    const [x, y, w, h] = WORN_BAND[slot];
    // collar/shoulder seam at the top, HEM at the bottom: a garment without a
    // hem is a rectangle of colour, and three of those stacked up read as a
    // debug overlay rather than as clothing.
    // A garment is not a band of colour with a hem: it has a closure down the
    // middle and a waist or a collar at the end that closure runs to. Three
    // stacked rectangles read as a debug overlay no matter how well they are
    // hemmed; the same three with a placket, a collar and a waistband read as
    // clothes on a person, which is what the centre column is for.
    const mid = x + w / 2;
    const detail = slot === 'pants'
      ? `<rect x="${x}" y="${y}" width="${w}" height="7" fill="rgba(0,0,0,.30)"/>` +
        `<rect x="${mid - 1.4}" y="${y + 7}" width="2.8" height="14" fill="rgba(0,0,0,.34)"/>` +
        `<rect x="${mid - 1.2}" y="${y + 30}" width="2.4" height="${h - 34}" fill="rgba(0,0,0,.22)"/>`
      : `<path d="M${mid - 15} ${y}L${mid} ${y + 13}L${mid + 15} ${y}" fill="none"
           stroke="rgba(0,0,0,.34)" stroke-width="3.4"/>` +
        `<rect x="${mid - 1.6}" y="${y + 12}" width="3.2" height="${h - 15}" fill="rgba(0,0,0,.36)"/>` +
        `<rect x="${mid - 1.6}" y="${y + 12}" width="1.2" height="${h - 15}" fill="rgba(255,255,255,.09)"/>`;
    return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${t}" opacity=".8"/>` +
      `<rect x="${x}" y="${y}" width="${w}" height="2.5" fill="rgba(0,0,0,.34)"/>` +
      detail +
      `<rect x="${x}" y="${y + h - 3}" width="${w}" height="3" fill="rgba(0,0,0,.28)"/>`;
  };
  const pack = tintOf('backpack');
  return `<svg class="body" viewBox="-16 0 152 260" xmlns="http://www.w3.org/2000/svg">
  <defs><clipPath id="doll-clip"><path d="${BODY_TOP}"/><path d="${BODY_FEET}"/></clipPath>
   <!-- Same lighting law as the item sheet (itemicons.js #ic-f-cyl): a body is
        an upright volume, so the ramp runs ACROSS it in user space with a
        bounce on the far edge, not diagonally across whatever box the
        silhouette happens to fill. The centre column was the one unlit object
        on a screen full of lit ones. -->
   <linearGradient id="doll-lit" gradientUnits="userSpaceOnUse" x1="4" y1="0" x2="116" y2="0">
     <stop offset="0" stop-color="#000000" stop-opacity=".40"/>
     <stop offset=".10" stop-color="#000000" stop-opacity=".12"/>
     <stop offset=".28" stop-color="#ffffff" stop-opacity=".17"/>
     <stop offset=".46" stop-color="#ffffff" stop-opacity=".03"/>
     <stop offset=".64" stop-color="#000000" stop-opacity=".08"/>
     <stop offset=".86" stop-color="#000000" stop-opacity=".34"/>
     <stop offset=".96" stop-color="#000000" stop-opacity=".46"/>
     <stop offset="1" stop-color="#ffffff" stop-opacity=".12"/></linearGradient>
   <linearGradient id="doll-ao" gradientUnits="userSpaceOnUse" x1="0" y1="118" x2="0" y2="258">
     <stop offset="0" stop-color="#000000" stop-opacity="0"/>
     <stop offset="1" stop-color="#000000" stop-opacity=".30"/></linearGradient></defs>
  ${pack ? `<g opacity=".72"><rect x="24" y="44" width="72" height="86" rx="16" fill="${pack}"/>
    <rect x="24" y="44" width="72" height="86" rx="16" fill="url(#doll-lit)"/>
    <rect x="24" y="86" width="72" height="5" fill="rgba(0,0,0,.34)"/>
    <rect x="24" y="44" width="72" height="86" rx="16" fill="none" stroke="rgba(0,0,0,.45)" stroke-width="2"/></g>` : ''}
  <g class="silhouette"><path d="${BODY_TOP}"/><path d="${BODY_FEET}"/></g>
  <g clip-path="url(#doll-clip)">${band('shirt')}${band('jacket')}${band('pants')}
    ${pack ? `<g fill="none" stroke="${pack}" stroke-width="9" opacity=".7" stroke-linecap="round">
      <path d="M45 40c-6 18-6 42 0 62"/><path d="M75 40c6 18 6 42 0 62"/></g>` : ''}
    <path d="${BODY_TOP}" fill="url(#doll-lit)"/><path d="${BODY_FEET}" fill="url(#doll-lit)"/>
    <path d="${BODY_TOP}" fill="url(#doll-ao)"/><path d="${BODY_FEET}" fill="url(#doll-ao)"/></g>
  <g class="silhouette-line" clip-path="url(#doll-clip)"><path d="${BODY_TOP}"/><path d="${BODY_FEET}"/></g>
 </svg>`;
}

// value -> vitals step color. Colour is RESERVED for degraded states: a healthy
// survivor's column is four bone-white glyphs, never a saturated traffic light.
const vitalColor = (v) =>
  v > 55 ? 'var(--hud-bone)' : v > 32 ? 'var(--hud-warn)' : v > 16 ? 'var(--hud-bad)' : 'var(--hud-crit)';

export class InventoryUI {
  // handlers: { eat(it, def), drop(it), equip(it), patch(it, def), cook(...),
  //             canCook(), lightFire(), vicinity()?, hands()? }
  constructor(inventory, stats, handlers, canvas) {
    this.inv = inventory;
    this.stats = stats;
    this.h = handlers;
    this.canvas = canvas;
    this.open = false;
    this.drag = null; // {it, ghost}
    // CI hook: window.__game.invUI.missingIcons() must return [] — a new item
    // without hand-authored art falls back to a generic crate, and that should
    // fail a smoke run rather than ship silently.
    this.missingIcons = missingIcons;
    // second CI hook: two items may not share a silhouette AND a near-identical
    // tint AND a footprint. Round 4 shipped 5 identical cans and 10 identical
    // mushrooms separated only by hue; this must stay [].
    this.iconCollisions = iconCollisions;
    this._css();
    this._build();
    inventory.onChange = () => { if (this.open) this.render(); this.renderQuickbar(); };
  }

  // How many quickslots the player actually has: the floor every survivor
  // carries plus whatever the clothing adds, capped by the number of keys the
  // quickbar array holds. ONE definition, used by the bar, the context menu
  // and useQuickslot() — they disagreed before, which is how a slot could be
  // assignable and then refuse to fire.
  quickslotCount() {
    const n = BASE_QUICKSLOTS + (this.inv.quickslotCount() || 0);
    return Math.max(BASE_QUICKSLOTS, Math.min(this.inv.quickbar.length, n));
  }

  // grid cell size in px — read back from --ui-cell so the JS grid math and
  // the CSS agree at every resolution (the token scales with the frame).
  _cs() {
    if (!this._probe) {
      this._probe = el('div');
      this._probe.style.cssText = 'position:absolute;visibility:hidden;width:var(--ui-cell);height:0';
      document.body.appendChild(this._probe);
    }
    return Math.round(this._probe.getBoundingClientRect().width) || 54;
  }

  _css() {
    injectTheme();
    const css = document.createElement('style');
    css.textContent = `
      /* The scrim is the frame this screen hangs in. At rgba(0,0,0,.5) +
         blur(9px) the town behind it stayed legible right through the
         container column — ground props and vegetation reading through your
         backpack is the "web app in a browser tab" tell. DayZ drops the world
         to a near-black, desaturated field and lets the panels own the screen. */
      #inv-root { position:fixed; inset:0; z-index:20; display:none;
                  background:rgba(0,0,0,.74); backdrop-filter:blur(18px) saturate(.5);
                  -webkit-backdrop-filter:blur(18px) saturate(.5);
                  font:400 var(--ui-s)/1.4 var(--ui-font); letter-spacing:var(--ui-track);
                  color:var(--hud-text); padding:calc(20px * var(--ui-scale)) calc(26px * var(--ui-scale));
                  overflow:hidden; }
      .inv-top { display:flex; justify-content:space-between; align-items:baseline;
                 max-width:1560px; margin:0 auto calc(10px * var(--ui-scale)); }
      .inv-top .t { font-size:var(--ui-m); font-weight:600; letter-spacing:.26em; color:var(--ui-hi); }
      .inv-top .c { font-size:var(--ui-xs); letter-spacing:.16em; color:var(--hud-dim);
                    text-transform:uppercase; }
      .inv-cols { display:grid; grid-template-columns:.86fr 1fr 1.32fr;
                  gap:calc(14px * var(--ui-scale)); max-width:1560px; margin:0 auto;
                  height:calc(100vh - 78px * var(--ui-scale)); }
      /* DayZ chrome: flat dark slabs — square corners, hairline borders */
      .panel { background:var(--panel-bg); border:1px solid var(--panel-line);
               display:flex; flex-direction:column; min-height:0; }
      /* a panel that only has a few rows of content stops at those rows instead
         of stretching a checkerboard down the whole column */
      .panel.shrink { align-self:start; max-height:100%; }
      .pcap { background:var(--panel-cap); border-bottom:1px solid var(--panel-line);
              padding:calc(5px * var(--ui-scale)) calc(10px * var(--ui-scale));
              font-size:var(--ui-xs); font-weight:600; letter-spacing:.16em;
              text-transform:uppercase; color:#9aa0a6; display:flex;
              justify-content:space-between; flex:none; }
      .pbody { padding:calc(10px * var(--ui-scale)); overflow:auto; flex:1; min-height:0; }
      /* --- vicinity (ground): the SAME grid components as the containers.
         A text list broke the drag metaphor and read as a settings menu
         bolted onto a loot screen. --- */
      .vempty { color:#71766e; font-size:var(--ui-s); padding:calc(6px * var(--ui-scale)); }
      /* --- character column: hands slot + paperdoll + worn garments --- */
      .hands { display:flex; align-items:center; gap:calc(10px * var(--ui-scale));
               border:1px solid var(--panel-line); background:rgba(255,255,255,.035);
               padding:calc(7px * var(--ui-scale)) calc(10px * var(--ui-scale)); flex:none; }
      .hands .ico { width:calc(40px * var(--ui-scale)); height:calc(40px * var(--ui-scale));
                    filter:drop-shadow(0 1px 2px rgba(0,0,0,.6)); }
      .hands .hl { font-size:var(--ui-xs); letter-spacing:.16em; color:#9aa0a6;
                   text-transform:uppercase; }
      .hands .hv { font-size:var(--ui-m); color:var(--ui-hi); }
      .hands.empty .hv { color:#71766e; }
      .doll { position:relative; flex:1; min-height:0; margin-top:calc(10px * var(--ui-scale)); }
      /* survivor state strip along the bottom of the character column — the
         same four vitals as the always-on column, spelled out while the screen
         is open (and it stops the column from being dead canvas) */
      .vstrip { display:flex; gap:calc(14px * var(--ui-scale)); flex:none;
                border-top:1px solid var(--panel-line); padding-top:calc(9px * var(--ui-scale));
                margin-top:calc(9px * var(--ui-scale)); }
      .vstat { flex:1; min-width:0; }
      .vstat .vh { display:flex; align-items:center; gap:calc(6px * var(--ui-scale));
                   font-size:var(--ui-xs); letter-spacing:.14em; text-transform:uppercase;
                   color:#8b9088; }
      .vstat .vic2 { width:calc(15px * var(--ui-scale)); height:calc(15px * var(--ui-scale)); flex:none; }
      .vstat .track { height:3px; background:rgba(255,255,255,.09); margin-top:5px; }
      .vstat .track i { display:block; height:100%; }
      /* The figure has to fit BETWEEN the two slot columns, not run underneath
         them. At 84% the body was wide enough that all eight labelled slots sat
         on top of it, and a translucent panel over a translucent mannequin is
         what made the centre column read as layered glass rather than as a
         dressed survivor with his kit called out beside him. */
      .doll .body { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%);
                    height:74%; }
      .doll .body { opacity:.94; filter:drop-shadow(0 6px 14px rgba(0,0,0,.55)); }
      /* the mannequin has to survive the darker scrim: at .14 alpha over the
         old .5 backdrop it was a ghost, and the largest element on the screen
         cannot be the faintest one */
      .doll .body .silhouette { fill:rgba(46,50,45,.9); }
      .doll .body .silhouette-line { fill:none; stroke:rgba(214,222,210,.24); stroke-width:1.6; }
      .eq-slot { position:absolute; width:calc(146px * var(--ui-scale));
                 border:1px solid var(--panel-line); background:rgba(13,13,13,.94);
                 padding:calc(6px * var(--ui-scale)) calc(8px * var(--ui-scale));
                 cursor:pointer; display:flex; gap:calc(8px * var(--ui-scale)); align-items:center; }
      /* leader line from the slot to the body — the gaps between rows are now
         a labelled diagram instead of three bands of dead air */
      .eq-slot::after { content:''; position:absolute; top:50%;
                        width:calc(46px * var(--ui-scale)); height:1px; }
      .eq-slot.l::after { left:100%;
        background:linear-gradient(90deg,rgba(255,255,255,.24),rgba(255,255,255,0)); }
      .eq-slot.r::after { right:100%;
        background:linear-gradient(270deg,rgba(255,255,255,.24),rgba(255,255,255,0)); }
      .eq-slot:hover { border-color:rgba(255,255,255,.26); }
      .eq-slot.held { border-color:rgba(216,200,106,.45);
                      box-shadow:inset 0 0 0 1px rgba(216,200,106,.14); }
      .eq-slot .ico { width:calc(30px * var(--ui-scale)); height:calc(30px * var(--ui-scale)); flex:none;
                      filter:drop-shadow(0 1px 2px rgba(0,0,0,.6)); }
      .eq-slot .txt { min-width:0; flex:1; }
      .eq-slot .lbl { color:#8b9088; font-size:var(--ui-xs); font-weight:600;
                      text-transform:uppercase; letter-spacing:.14em; }
      .eq-slot .val { color:var(--ui-hi); font-size:var(--ui-s); white-space:nowrap;
                      overflow:hidden; text-overflow:ellipsis; }
      .eq-slot.empty .val { color:#6b7068; }
      /* what this garment holds and what it costs to wear — the fill bar alone
         says "fairly full" and nothing else */
      .eq-slot .sub { display:flex; justify-content:space-between; gap:6px;
                      font-size:var(--ui-xs); letter-spacing:.08em; color:var(--hud-dim);
                      margin-top:1px; }
      .eq-slot .sub .k { color:#71766e; }
      .fill { height:3px; background:rgba(255,255,255,.09); margin-top:4px; }
      .fill i { display:block; height:100%; background:rgba(200,210,190,.5); }
      /* --- container grids --- */
      .conts { display:flex; flex-wrap:wrap; gap:calc(16px * var(--ui-scale)); align-content:flex-start; }
      /* the caption must never be wider than the grid it caps: the box gets an
         explicit measured width, the caption fills it, the label truncates */
      .cont { flex:none; }
      .cont .cap { width:100%; color:#9aa0a6; font-size:var(--ui-xs); font-weight:600;
                   text-transform:uppercase; letter-spacing:.14em;
                   background:var(--panel-cap); border:1px solid var(--panel-line);
                   border-bottom:none; padding:3px 8px 2px; box-sizing:border-box; }
      .cont .cap .cn { display:block; min-width:0; overflow:hidden; color:var(--ui-hi);
                       text-overflow:ellipsis; white-space:nowrap; }
      /* capacity + mass. The NUMERALS are data and take --ui-hi; the unit and
         the separator stay chrome. This is also the line that made the icon
         brightness clamp in itemicons.js necessary — if decorative art can hit
         244 as well, the tier this token defines stops meaning anything. */
      .cont .cap .cmeta { display:flex; justify-content:space-between; gap:6px;
                          margin-top:1px; font-weight:500; letter-spacing:.08em; }
      .cont .cap .ccap { color:var(--ui-hi); }
      .cont .cap .ckg { color:#8b9088; }
      .cont .cap .fill { margin:3px 0 1px; }
      .grid { position:relative; background:rgba(6,6,6,.66); border:1px solid var(--panel-line); }
      .cell { position:absolute; border:1px solid rgba(255,255,255,.055); box-sizing:border-box; }
      /* The item plate is OPAQUE. A translucent plate let the .cell hairlines
         show straight through every multi-cell item — a vertical line bisected
         the 2x2 rain jacket in round-004's inventory.png. An item owns its rect. */
      .item { position:absolute; box-sizing:border-box; border:1px solid rgba(255,255,255,.13);
              background:linear-gradient(180deg,#232320,#151513);
              cursor:grab; overflow:hidden; user-select:none;
              display:flex; align-items:center; justify-content:center; }
      .item:hover { border-color:rgba(255,255,255,.38);
                    background:linear-gradient(180deg,#33332e,#212120); }
      /* AN ITEM FILLS ITS FOOTPRINT. Round 5 clamped every icon to one cell
         (max-width/height: --ui-cell * 1.05) to keep "optical mass constant",
         and the result was a 4x4 hiking backpack drawing a one-cell glyph in
         the middle of an enormous dead plate — the single loudest tell that
         this was a web grid and not DayZ's inventory, where the item render
         spans its whole footprint and the grid reads as physical volume.
         The size is set per item in _itemEl(): the glyph canvas (see
         iconAspect) is fitted into the plate at ICON_FILL. */
      .item .ico { filter:drop-shadow(0 2px 3px rgba(0,0,0,.6)); }
      .item.ruined .ico { opacity:.45; }
      .item .qty { position:absolute; right:0; bottom:4px; padding:0 4px 0 5px;
                   background:rgba(0,0,0,.78); color:var(--ui-hi); font-weight:600;
                   font-size:var(--ui-s); line-height:1.35;
                   text-shadow:0 1px 2px rgba(0,0,0,.9); }
      .cbar { position:absolute; left:0; right:0; bottom:0; height:3px; background:rgba(0,0,0,.55); }
      .cbar.heavy { height:4px; }
      .cbar i { display:block; height:100%; }
      .item.ground { cursor:pointer; }
      /* A REFUSED DROP HAS TO SAY SO. The handler used to bail out on every
         awkward drop and Inventory.move()'s false was thrown away, so "that
         does not fit" and "the drag handler is broken" looked identical — the
         player's only feedback was the item snapping home. Two signals, both
         quiet: the grid that said no flashes its border, and one line of text
         names the reason. Both live only on the Tab screen, over a .74 black
         scrim, so neither can become the brightest cluster in a game frame. */
      .grid.deny { animation:inv-deny .42s ease-out; }
      @keyframes inv-deny {
        0%   { box-shadow:inset 0 0 0 1px rgba(217,68,52,.75); }
        100% { box-shadow:inset 0 0 0 1px rgba(217,68,52,0); }
      }
      #inv-deny { position:fixed; left:50%; bottom:calc(34px * var(--ui-scale));
                  transform:translateX(-50%); z-index:21; display:none;
                  padding:calc(5px * var(--ui-scale)) calc(12px * var(--ui-scale));
                  background:rgba(10,10,10,.92); border:1px solid rgba(192,116,52,.45);
                  color:#c9a06a; font:400 var(--ui-s)/1.3 var(--ui-font);
                  letter-spacing:var(--ui-track); pointer-events:none; }
      /* ONE axis owns coloured chrome and it is CONDITION (the DayZ signal).
         Round 4 also tinted border-left by category, so a coloured stripe on an
         item could mean "food" or "damaged" and the player could not know which.
         Category is carried by the silhouette now — not by a second colour code. */
      /* --- item tooltip: the name/quantity/condition panel that replaced four
         native title attributes (OS chrome: wrong font, 1s delay, invisible
         under pointer lock and in every press capture) --- */
      #inv-tip { position:fixed; z-index:24; display:none; pointer-events:none;
                 background:rgba(13,13,13,.97); border:1px solid rgba(255,255,255,.15);
                 padding:calc(7px * var(--ui-scale)) calc(11px * var(--ui-scale));
                 font:400 var(--ui-s)/1.45 var(--ui-font); letter-spacing:var(--ui-track);
                 color:var(--hud-text); max-width:calc(280px * var(--ui-scale));
                 box-shadow:0 6px 20px rgba(0,0,0,.65); }
      #inv-tip .tn { font-size:var(--ui-m); font-weight:600; color:var(--ui-hi); }
      #inv-tip .tn b { color:#9aa0a6; font-weight:400; }
      #inv-tip .tm { display:flex; gap:calc(10px * var(--ui-scale)); margin-top:2px;
                     font-size:var(--ui-xs); letter-spacing:.14em; text-transform:uppercase; }
      #inv-tip .twt { color:var(--hud-dim); }
      #inv-tip .tsz { color:#71766e; }
      #inv-tip .td { color:#9aa0a6; margin-top:calc(5px * var(--ui-scale));
                     border-top:1px solid rgba(255,255,255,.08);
                     padding-top:calc(5px * var(--ui-scale)); }
      #inv-menu { position:fixed; z-index:22; background:rgba(14,14,14,.96);
                  border:1px solid rgba(255,255,255,.12); border-radius:0;
                  min-width:150px; display:none; font:400 var(--ui-s)/1.4 var(--ui-font);
                  letter-spacing:var(--ui-track); }
      #inv-menu div { padding:6px 12px; cursor:pointer; color:var(--hud-text); }
      #inv-menu div:hover { background:rgba(255,255,255,.10); }
      #inv-ghost { position:fixed; z-index:23; pointer-events:none; opacity:.85; }
      /* --- vitals: DayZ iconized column, bottom-right, no labels/numbers ---
         The 24px viewBox leaves ~4px optical padding inside each icon; pull the
         column 4px past the margin so the OPTICAL edge (not the box edge) lines
         up with the ammo block's text edge on the opposite corner. */
      #vitals { position:fixed; right:var(--hud-margin);
                bottom:var(--hud-margin); z-index:10;
                display:flex; flex-direction:column-reverse;
                gap:calc(11px * var(--ui-scale)); align-items:center; }
      /* NO PLATES. Round 4 boxed each vital in a 34px chip with a border and a
         .44 black fill, and the column became four chiclets that out-shouted
         the world. These are bare glyphs again (round-003's reading), held up
         by a double drop-shadow instead of a box, with a full-width value bar
         underneath so state is never encoded by colour alone. */
      /* OFF-NOMINAL ONLY. Round 5 drew all four glyphs every tick regardless of
         value, so a survivor in perfect health permanently carried four
         stickers in the corner — and that rail, per the blind judge, was one of
         the two things that identified our frames on sight. DayZ's status icons
         are ABSENT while nominal and arrive when something goes wrong; their
         absence is most of what "diegetic minimal" means. A slot that is not
         speaking is not in the DOM flow at all (the column reflows around it),
         and one that starts speaking fades up over 400 ms instead of popping. */
      .vslot { position:relative; width:calc(24px * var(--ui-scale));
               display:none; flex-direction:column; align-items:center;
               gap:calc(3px * var(--ui-scale)); }
      .vslot.on { display:flex; animation:vfade .4s ease-out; }
      @keyframes vfade { from { opacity:0; transform:translateY(5px); }
                         to { opacity:1; transform:none; } }
      .vslot .vv { display:block; width:100%; height:2px; }
      #vitals .vic { width:calc(24px * var(--ui-scale)); height:calc(24px * var(--ui-scale)); opacity:1;
                     filter:drop-shadow(0 1px 2px rgba(0,0,0,.95))
                            drop-shadow(0 0 3px rgba(0,0,0,.75)); }
      #vitals .vic.blink { animation:vblink .9s infinite; }
      @keyframes vblink { 0%,100% { opacity:1; } 50% { opacity:.2; } }
      /* same asymmetry as the quickbar: snap back with no transition at all,
         drift away over .7 s (the transition lives on the hud-idle rule) */
      /* the warnings settle less than the rest of the HUD — they are the
         channel that says something is wrong — and a critical vital or an open
         wound does not settle at all */
      body.hud-idle #vitals { opacity:.7; transition:opacity .7s ease-out; }
      body.hud-idle #vitals.crit { opacity:1; }
      /* --- quickbar: icon-only slots, key numeral, qty chip bottom-right --- */
      /* WAKING IS NOT THE REVERSE OF SETTLING, AND IT IS NOT A TRANSITION.
         The transition that runs is the one declared in the state being
         ENTERED, so the two directions are declared separately: settling into
         hud-idle drifts out over 700 ms (below), and waking out of it has no
         transition at all, so opacity snaps to 1 in the same style recalc that
         drops the class.
         That is not a stylistic choice, it is a correctness one. Probed against
         the running game (three runs, tools in scratchpad) a symmetric fade
         left the hotbar at computed opacity 0 for more than SEVEN SECONDS after
         a real keypress, because a CSS transition only advances on frames the
         document actually paints and this build renders at a fraction of a
         frame per second under load. A wake that depends on the compositor
         ticking is a wake that can silently not happen; a wake that is a plain
         value change cannot. Only the fade-OUT, where a dropped frame is
         invisible, is allowed to interpolate. */
      /* pointer-events:none, ALWAYS. The slots have no click handler (the bar
         is driven by keys 8/9/0/-), but the row sits dead centre at the bottom
         of the frame — exactly where a player clicks to take pointer lock back
         after the inventory or the F2 panel released it. Only the hud-idle
         rule set this, so a freshly-poked hotbar was a click dead zone. */
      #quickbar { position:fixed; left:50%; bottom:var(--hud-margin); transform:translateX(-50%);
                  z-index:10; display:flex; gap:calc(4px * var(--ui-scale));
                  pointer-events:none;
                  font:400 var(--ui-xs)/1.2 var(--ui-font); letter-spacing:var(--ui-track); }
      /* Settled hotbar. DayZ's fades out ENTIRELY a few seconds after you stop
         using it. Round 6 wrote that sentence in this comment and then kept a
         .16 "trace" so the slots would stay "findable" — and .16 over the
         bottom-centre of a lit frame is not a findable slot, it is an
         unexplained grey rectangle floating in the grass, which reads as a
         compression artifact rather than as UI (see round-006/town_street.png
         and third_person_weapon.png). A HUD element that cannot be identified
         is not quieter than no HUD element, it is worse than one.
         So it goes. The affordance the trace was supposed to buy is already
         there: any key, click or wheel notch fades the whole row back in over
         550 ms, which is faster than a player can look down. */
      body.hud-idle #quickbar { opacity:0; pointer-events:none; transition:opacity .7s ease-out; }
      .qslot { width:var(--ui-qslot); height:var(--ui-qslot); background:rgba(6,6,6,.5);
               border:1px solid rgba(255,255,255,.11); position:relative; overflow:hidden;
               color:var(--hud-text); display:flex; align-items:center; justify-content:center;
               box-shadow:0 1px 5px rgba(0,0,0,.5);
               transition:border-color .12s, box-shadow .12s; }
      /* an empty slot must not carry the same weight as a stocked one — it
         recedes to a dashed hairline so the filled slots lead the eye */
      .qslot.empty { border:1px dashed rgba(255,255,255,.07); background:rgba(6,6,6,.3);
                     opacity:.5; box-shadow:none; }
      .qslot.on { border-color:rgba(255,255,255,.5);
                  box-shadow:inset 0 0 0 1px rgba(255,255,255,.16),
                             0 0 0 1px rgba(0,0,0,.55), 0 2px 9px rgba(0,0,0,.55); }
      .qslot .ico { width:76%; height:76%; filter:drop-shadow(0 2px 3px rgba(0,0,0,.6)); }
      /* keycap plate: a bare "-" glyph floating in an empty box read as a
         missing-glyph error in the round-003 captures */
      .qslot .key { position:absolute; left:3px; top:3px; min-width:calc(13px * var(--ui-scale));
                    height:calc(13px * var(--ui-scale)); line-height:calc(13px * var(--ui-scale));
                    text-align:center; padding:0 2px; box-sizing:border-box;
                    background:rgba(0,0,0,.42); border:1px solid rgba(255,255,255,.09);
                    color:#9aa0a6; font-weight:600; font-size:calc(10px * var(--ui-scale));
                    text-shadow:none; }
      .qslot .qqty { position:absolute; right:0; bottom:3px; padding:0 3px 0 4px;
                     background:rgba(0,0,0,.7); color:var(--ui-hi); font-weight:600;
                     text-shadow:var(--hud-shadow); }
      /* --- interact prompt: borderless, keycap chip, bottom-center --- */
      #interact-prompt { position:fixed; left:50%; bottom:calc(88px * var(--ui-scale));
                         transform:translateX(-50%);
                         z-index:10; font:400 var(--ui-m)/1.4 var(--ui-font);
                         letter-spacing:var(--ui-track); color:var(--hud-text);
                         text-shadow:var(--hud-shadow); display:none; white-space:nowrap; }
    `;
    document.head.appendChild(css);
  }

  _build() {
    for (const markup of [VITAL_DEFS, ITEM_ICON_DEFS]) {
      const holder = document.createElement('div');
      holder.innerHTML = markup;
      document.body.appendChild(holder.firstElementChild);
    }
    this.root = el('div'); this.root.id = 'inv-root';
    document.body.appendChild(this.root);
    this.menu = el('div'); this.menu.id = 'inv-menu';
    document.body.appendChild(this.menu);
    this.vitals = el('div'); this.vitals.id = 'vitals';
    document.body.appendChild(this.vitals);
    this.qb = el('div'); this.qb.id = 'quickbar';
    document.body.appendChild(this.qb);
    this.prompt = el('div'); this.prompt.id = 'interact-prompt';
    document.body.appendChild(this.prompt);
    this.tip = el('div'); this.tip.id = 'inv-tip';
    document.body.appendChild(this.tip);
    this.deny = el('div'); this.deny.id = 'inv-deny';
    document.body.appendChild(this.deny);

    document.addEventListener('mousedown', (e) => {
      if (!this.menu.contains(e.target)) this.menu.style.display = 'none';
    });
    document.addEventListener('mousemove', (e) => this._dragMove(e));
    document.addEventListener('mouseup', (e) => this._dragEnd(e));
    this.renderQuickbar();
  }

  // ---------- in-game item tooltip ----------
  // Replaces four `title=` call sites. Since the vicinity list lost its item
  // names in r3 this is the ONLY identification path for a grid of silhouettes,
  // so it cannot be OS chrome: it is a #inv-menu-styled panel, ~120 ms delay,
  // and it survives a press capture.
  //   spec: { name, qty, cond (0..1) | null, size:[w,h] | null, desc }
  _tip(node, spec) {
    node.addEventListener('mouseenter', () => {
      clearTimeout(this._tipT);
      if (this.drag) return; // never over a dragged item
      this._tipT = setTimeout(() => { if (!this.drag) this.showTip(spec); }, 120);
    });
    node.addEventListener('mouseleave', () => {
      clearTimeout(this._tipT);
      this.tip.style.display = 'none';
    });
    node.addEventListener('mousemove', (e) => this._tipMove(e));
    return node;
  }

  // exposed so a capture harness (or a debug console) can show the panel
  // without a real pointer — headless screenshots have no hover
  showTip(spec) {
    const t = this.tip;
    t.innerHTML = '';
    const nm = el('div', 'tn', spec.name);
    if (spec.qty > 1) { const b = el('b', null, ' ×' + spec.qty); nm.appendChild(b); }
    t.appendChild(nm);
    const meta = el('div', 'tm');
    if (spec.cond != null) {
      const step = condStep(spec.cond);
      const s = el('span', null, step[1]);
      s.style.color = step[2];
      meta.appendChild(s);
    }
    if (spec.wt != null) meta.appendChild(el('span', 'twt tnum', kg(spec.wt)));
    if (spec.size) meta.appendChild(el('span', 'tsz', `${spec.size[0]}×${spec.size[1]}`));
    if (meta.childNodes.length) t.appendChild(meta);
    if (spec.desc) t.appendChild(el('div', 'td', spec.desc));
    t.style.display = 'block';
    if (this._tipXY) this._place(this._tipXY[0], this._tipXY[1]);
    return t;
  }

  _tipMove(e) {
    this._tipXY = [e.clientX, e.clientY];
    if (this.tip.style.display === 'block') this._place(e.clientX, e.clientY);
  }

  _place(x, y) {
    const r = this.tip.getBoundingClientRect();
    this.tip.style.left = Math.min(x + 16, innerWidth - r.width - 8) + 'px';
    this.tip.style.top = Math.min(y + 18, innerHeight - r.height - 8) + 'px';
  }

  toggle() {
    this.open = !this.open;
    this.root.style.display = this.open ? 'block' : 'none';
    this.menu.style.display = 'none';
    this.tip.style.display = 'none';
    // ...and the refusal line, which outlived the screen by up to 1.6 s and
    // floated "That space is taken." over the gameplay frame
    clearTimeout(this._denyT);
    this.deny.style.display = 'none';
    if (this.open) {
      document.exitPointerLock?.();
      this.render();
    } else {
      pokeHud(); // coming back out of the screen, the hotbar is worth a look
      if (this.canvas) this.canvas.requestPointerLock?.();
    }
  }

  // ---------- item condition (public API for gameplay code) ----------
  setCondition(it, c) { it.cond = Math.max(0, Math.min(1, c)); if (this.open) this.render(); }
  damageItem(it, amt) { this.setCondition(it, condOf(it) - amt); }

  // ---------- full inventory screen ----------
  render() {
    const inv = this.inv;
    this.root.innerHTML = '';
    const top = el('div', 'inv-top');
    top.appendChild(el('span', 't', 'INVENTORY'));
    const close = el('span', 'c');
    close.innerHTML = '<span class="keycap">TAB</span>';
    close.appendChild(document.createTextNode(' CLOSE'));
    top.appendChild(close);
    this.root.appendChild(top);

    const cols = el('div', 'inv-cols');
    this.root.appendChild(cols);
    const vic = this._vicinityPanel();
    cols.appendChild(vic);
    cols.appendChild(this._characterPanel());
    cols.appendChild(this._containerPanel());
    // the ground grid needs its column's measured width, so it is filled after
    // the panel is in the document
    vic.fill?.();
  }

  _panel(caption, right) {
    const p = el('div', 'panel');
    const cap = el('div', 'pcap');
    cap.appendChild(el('span', null, caption));
    if (right) cap.appendChild(el('span', null, right));
    p.appendChild(cap);
    const body = el('div', 'pbody');
    p.appendChild(body);
    p.body = body;
    return p;
  }

  // capacity strip: used cells / total cells of a container
  _fill(used, total) {
    const f = el('div', 'fill');
    const i = el('i');
    i.style.width = Math.min(100, total ? (used / total) * 100 : 0) + '%';
    if (total && used >= total) i.style.background = 'rgba(216,200,106,.75)'; // full
    f.appendChild(i);
    return f;
  }

  _usedCells(items) {
    let n = 0;
    for (const it of items) { const s = ITEMS[it.id].size; n += s[0] * s[1]; }
    return n;
  }

  // first-fit footprint packer, row-major — the same spatial language as a
  // container grid, so ground loot is not a second layout path
  _pack(entries, cols) {
    const occ = [];
    const taken = (x, y, w, h) => {
      for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) if (occ[j] && occ[j][i]) return true;
      return false;
    };
    const out = [];
    let rows = 0;
    for (const e of entries) {
      const sz = ITEMS[e.id].size;
      const w = Math.min(sz[0], cols); const h = sz[1];
      let placed = false;
      for (let y = 0; y < 64 && !placed; y++) {
        for (let x = 0; x + w <= cols && !placed; x++) {
          if (taken(x, y, w, h)) continue;
          for (let j = y; j < y + h; j++) { occ[j] = occ[j] || []; for (let i = x; i < x + w; i++) occ[j][i] = 1; }
          out.push({ e, x, y, w, h });
          rows = Math.max(rows, y + h);
          placed = true;
        }
      }
    }
    return { out, rows };
  }

  // --- left column: what lies on the ground around the player, as a GRID ---
  _vicinityPanel() {
    const near = (this.h.vicinity?.() || []).slice(0, 32);
    const p = this._panel('Vicinity', near.length ? `${near.length}` : '');
    p.classList.add('shrink');
    // the whole column is the ground, not just its cells: with nothing lying
    // nearby there is no grid to aim at, and "drop it here" still has to work
    p.dataset.vicinity = '1';
    p.fill = () => {
      p.body.innerHTML = '';
      if (!near.length) {
        p.body.appendChild(el('div', 'vempty', 'Nothing within reach.'));
        p.body.appendChild(el('div', 'vempty', 'Drag an item here to drop it.'));
        return;
      }
      const CS = this._cs();
      const pad = 22 * (parseFloat(getComputedStyle(document.documentElement)
        .getPropertyValue('--ui-scale')) || 1);
      const avail = (p.body.clientWidth || 380) - pad;
      const cols = Math.max(3, Math.floor(avail / CS));
      const { out, rows } = this._pack(near.filter((g) => ITEMS[g.id]), cols);
      // Size the grid to its CONTENT plus one spare drop row. Round 4 stretched
      // it to the full column height, so nine items sat in the top three rows of
      // a 14-row checkerboard and the panel spent 75% of its area advertising
      // emptiness. DayZ only shows the rows that exist.
      const rowsShown = Math.max(1, rows + 1);
      const grid = el('div', 'grid');
      grid.style.width = cols * CS + 'px';
      grid.style.height = rowsShown * CS + 'px';
      // THE MISSING DATASET. _dragEnd() resolves a drop by reading
      // grid.dataset.slot and looking it up in inventory.containers(); the
      // vicinity grid had no dataset at all, so every drag into this panel
      // found `undefined`, matched no container and returned silently. The
      // ground is not a container, though — it has no slot to look up — so it
      // is marked for what it is and a drop here means DROP.
      grid.dataset.vicinity = '1';
      for (let y = 0; y < rowsShown; y++) {
        for (let x = 0; x < cols; x++) {
          const cell = el('div', 'cell');
          cell.style.cssText = `left:${x * CS}px;top:${y * CS}px;width:${CS}px;height:${CS}px`;
          cell.dataset.x = x; cell.dataset.y = y;
          grid.appendChild(cell);
        }
      }
      for (const q of out) grid.appendChild(this._groundEl(q, CS));
      p.body.appendChild(grid);
      const hint = el('div', 'vempty',
        out.length === near.length
          ? 'Left-click to take · drag here to drop.'
          : 'More loot out of reach.');
      p.body.appendChild(hint);
    };
    return p;
  }

  // a ground stack drawn with the container item components; the name lives in
  // the tooltip, exactly like a packed item. Left click picks it up.
  _groundEl({ e: g, x, y, w, h }, CS) {
    const def = ITEMS[g.id];
    const cls = def.kind === 'food' || def.kind === 'drink' ? 'food'
      : def.kind === 'tool' || def.kind === 'med' ? 'tool' : 'wear';
    const box = el('div', 'item ground ' + cls);
    box.style.cssText = `left:${x * CS + 1}px;top:${y * CS + 1}px;` +
      `width:${w * CS - 2}px;height:${h * CS - 2}px`;
    const ic = iconFor(g.id);
    box.appendChild(icon(ic.sym, 'ico', ic.tint));
    if (g.qty > 1) box.appendChild(el('span', 'qty tnum', String(g.qty)));
    this._tip(box, { name: def.name, qty: g.qty, size: def.size, wt: massOfStack(g), desc: def.desc });
    box.addEventListener('mousedown', (ev) => {
      if (ev.button !== 0) return;
      ev.preventDefault();
      g.take?.();
      this.render();
    });
    return box;
  }

  // --- center column: hands slot + paperdoll with the worn garments ---
  // everything the survivor is carrying: what is worn, plus what is in it
  _load() {
    let kgSum = 0;
    for (const slot of ['jacket', 'shirt', 'pants', 'backpack']) {
      const eq = this.inv.equipment[slot];
      if (eq) kgSum += massOf(eq.id);
    }
    for (const c of this.inv.containers()) kgSum += massOfItems(c.items);
    return kgSum;
  }

  _characterPanel() {
    const p = this._panel('Character', kg(this._load()));
    const hands = this.h.hands?.() || null;
    const hd = el('div', 'hands' + (hands ? '' : ' empty'));
    hd.appendChild(icon(hands ? (WEAPON_ICON[hands.weapon] || 'crate') : 'hand', 'ico',
      hands ? '#a9ae9e' : 'rgba(200,206,196,.35)'));
    const ht = el('div');
    ht.appendChild(el('div', 'hl', 'Hands'));
    ht.appendChild(el('div', 'hv', hands ? hands.label : 'Empty'));
    hd.appendChild(ht);
    p.body.appendChild(hd);
    p.body.style.display = 'flex';
    p.body.style.flexDirection = 'column';

    const doll = el('div', 'doll');
    // the mannequin wears the equipped garments, in their own icon tints
    doll.innerHTML = dollSvg((slot) => {
      const eq = this.inv.equipment[slot];
      return eq && ITEMS[eq.id] ? iconFor(eq.id).tint : null;
    });
    p.body.appendChild(doll);

    // Paperdoll: garments down the left, WEAPONS + ammo down the right. A
    // survival screen in a game with seven weapons cannot show four shirt
    // slots and nothing you can shoot with — and the two columns of four
    // anchored at percentages fill the column instead of leaving dead canvas.
    const held = this.h.hands?.() || null;
    const heldSlot = held ? (held.weapon === 'rifle' ? 'primary'
      : held.weapon === 'pistol' ? 'sidearm' : 'melee') : null;
    let ammoQty = 0;
    for (const c of this.inv.containers()) {
      for (const it of c.items) if (it.id === 'ammo_rifle' || it.id === 'ammo_pistol') ammoQty += it.qty || 1;
    }
    const GEAR = {
      primary: ['Primary', 'rifle'], sidearm: ['Sidearm', 'pistol'],
      melee: ['Melee', 'knife'], ammo: ['Ammo', 'ammo_r'],
    };
    const WEAR_ICO = { jacket: 'jacket', shirt: 'tshirt', pants: 'pants', backpack: 'bag_hiking' };
    const ROWS = ['2%', '26%', '50%', '74%'];
    const addSlot = (side, row, sym, tint, label, value, dim, fill, tip, sub) => {
      const s = el('div', 'eq-slot ' + side + (dim ? ' empty' : ''));
      s.style.cssText = `${side === 'l' ? 'left:0' : 'right:0'}; top:${row}`;
      s.appendChild(icon(sym, 'ico', tint));
      const txt = el('div', 'txt');
      txt.appendChild(el('div', 'lbl', label));
      txt.appendChild(el('div', 'val', value));
      if (sub) {
        const sr = el('div', 'sub');
        sr.appendChild(el('span', 'tnum', sub[0]));
        sr.appendChild(el('span', 'k tnum', sub[1]));
        txt.appendChild(sr);
      }
      if (fill) txt.appendChild(fill);
      s.appendChild(txt);
      if (tip) this._tip(s, { name: value, desc: tip });
      doll.appendChild(s);
      return s;
    };
    ['jacket', 'shirt', 'pants', 'backpack'].forEach((slot, i) => {
      const eq = this.inv.equipment[slot];
      const def = eq && ITEMS[eq.id];
      const ic = def ? iconFor(eq.id) : null;
      const cells = def ? def.grid[0] * def.grid[1] : 0;
      addSlot('l', ROWS[i], ic ? ic.sym : WEAR_ICO[slot], ic ? ic.tint : 'rgba(200,206,196,.22)',
        slot, def ? def.name : '—', !def,
        def ? this._fill(this._usedCells(eq.items), cells) : null,
        def ? (def.desc || def.name) : null,
        def ? [`${this._usedCells(eq.items)}/${cells}`,
          kg(massOf(eq.id) + massOfItems(eq.items))] : null);
    });
    ['primary', 'sidearm', 'melee', 'ammo'].forEach((key, i) => {
      const [label, sym] = GEAR[key];
      if (key === 'ammo') {
        addSlot('r', ROWS[i], 'ammo_r', ammoQty ? '#9c8045' : 'rgba(200,206,196,.22)',
          label, ammoQty ? `${ammoQty} rounds` : '—', !ammoQty, null,
          'Loose ammunition carried in your clothing');
        return;
      }
      const on = heldSlot === key;
      const s = addSlot('r', ROWS[i], sym, on ? '#a9ae9e' : 'rgba(200,206,196,.22)',
        label, on ? held.label : '—', !on, null, on ? 'Currently in your hands' : null);
      if (on) s.classList.add('held');
    });

    const st = this.stats || {};
    const strip = el('div', 'vstrip');
    for (const [sym, label, v] of [['vi-heart', 'Health', st.health], ['vi-blood', 'Blood', st.blood],
      ['vi-food', 'Hunger', st.hunger], ['vi-drop', 'Thirst', st.thirst]]) {
      const val = Math.max(0, Math.min(100, Math.round(v ?? 100)));
      const col = vitalColor(val);
      const w = el('div', 'vstat');
      const head = el('div', 'vh');
      const g = document.createElementNS(NS, 'svg');
      g.setAttribute('class', 'vic2');
      g.style.color = col;
      const u = document.createElementNS(NS, 'use');
      u.setAttribute('href', '#' + sym);
      g.appendChild(u);
      head.appendChild(g);
      head.appendChild(el('span', null, label));
      w.appendChild(head);
      const track = el('div', 'track');
      const fi = el('i');
      fi.style.width = val + '%';
      fi.style.background = col;
      track.appendChild(fi);
      w.appendChild(track);
      strip.appendChild(w);
    }
    p.body.appendChild(strip);
    return p;
  }

  // --- right column: the grids of everything worn ---
  _containerPanel() {
    const CS = this._cs();
    const conts = this.inv.containers();
    const p = this._panel('Clothing & Containers');
    const wrap = el('div', 'conts');
    p.body.appendChild(wrap);
    for (const c of conts) {
      const [gw, gh] = c.grid;
      const box = el('div', 'cont');
      box.style.width = gw * CS + 'px'; // caption and grid share ONE measured width
      const cap = el('div', 'cap');
      cap.appendChild(el('span', 'cn', c.label));
      // A container that reports only a fill bar reports half of what a
      // survival inventory is for: how full it is AND what carrying it costs.
      // Cells used and mass carried, on their own line so a 2-cell-wide
      // container's caption never has to fit both on one.
      // The mass is the GARMENT PLUS ITS CONTENTS, identical to the figure the
      // matching equipped slot prints in the character column — two numbers on
      // one screen under the same word have to mean the same thing, and the
      // question a survivor is asking ("what is this jacket costing me") is
      // answered by the total, not by the pockets alone.
      const used = this._usedCells(c.items);
      const meta = el('div', 'cmeta');
      meta.appendChild(el('span', 'ccap tnum', `${used}/${gw * gh}`));
      meta.appendChild(el('span', 'ckg tnum', kg(massOf(c.eq.id) + massOfItems(c.items))));
      cap.appendChild(meta);
      cap.appendChild(this._fill(used, gw * gh));
      box.appendChild(cap);
      const grid = el('div', 'grid');
      grid.style.width = gw * CS + 'px';
      grid.style.height = gh * CS + 'px';
      grid.dataset.slot = c.slot;
      for (let y = 0; y < gh; y++) {
        for (let x = 0; x < gw; x++) {
          const cell = el('div', 'cell');
          cell.style.cssText = `left:${x * CS}px;top:${y * CS}px;width:${CS}px;height:${CS}px`;
          cell.dataset.x = x; cell.dataset.y = y; cell.dataset.slot = c.slot;
          grid.appendChild(cell);
        }
      }
      for (const it of c.items) grid.appendChild(this._itemEl(it, CS));
      box.appendChild(grid);
      wrap.appendChild(box);
    }
    return p;
  }

  // icon-first item cell: silhouette fills the footprint, name lives in the
  // tooltip, quantity chip bottom-right, condition bar along the bottom edge
  _itemEl(it, CS) {
    const def = ITEMS[it.id];
    const box = el('div');
    const cls = def.kind === 'food' || def.kind === 'drink' ? 'food'
      : def.kind === 'tool' || def.kind === 'med' ? 'tool' : 'wear';
    const c = condOf(it);
    box.className = 'item ' + cls + (c <= 0.18 ? ' ruined' : '');
    box.style.cssText = `left:${it.x * CS + 1}px;top:${it.y * CS + 1}px;` +
      `width:${def.size[0] * CS - 2}px;height:${def.size[1] * CS - 2}px`;
    const ic = iconFor(it.id);
    const glyph = icon(ic.sym, 'ico', ic.tint);
    // fit the glyph canvas into the plate: the item OWNS its footprint. A 4x4
    // pack draws four cells wide, a 1x2 bottle draws one wide and two tall.
    const pw = def.size[0] * CS - 2, ph = def.size[1] * CS - 2;
    const ar = iconAspect(ic.sym);
    let iw = pw * ICON_FILL, ih = iw / ar;
    if (ih > ph * ICON_FILL) { ih = ph * ICON_FILL; iw = ih * ar; }
    glyph.style.width = iw.toFixed(1) + 'px';
    glyph.style.height = ih.toFixed(1) + 'px';
    box.appendChild(glyph);
    if (it.qty > 1) box.appendChild(el('span', 'qty tnum', String(it.qty)));
    // Pristine gear shows NO bar (DayZ) — the bar is the damage signal, and a
    // 0.95-condition item painting a full-width bright bar was round 4's worst
    // HUD offender. It only appears once the item has left Pristine.
    const cbar = condBarEl(c);
    if (cbar) box.appendChild(cbar);
    this._tip(box, { name: def.name, qty: it.qty, cond: c, size: def.size, wt: massOfStack(it), desc: def.desc });
    box.addEventListener('mousedown', (e) => { if (e.button === 0) this._dragStart(e, it, box); });
    box.addEventListener('contextmenu', (e) => { e.preventDefault(); this._openMenu(e, it); });
    return box;
  }

  // ---------- drag & drop ----------
  _dragStart(e, it, source) {
    e.preventDefault();
    clearTimeout(this._tipT);
    this.tip.style.display = 'none';
    const ghost = source.cloneNode(true);
    ghost.id = 'inv-ghost';
    document.body.appendChild(ghost);
    // the ORIGIN container, captured now: a swap has to know whether the two
    // items share a grid, and after the drop the model may already have moved
    // the record
    const from = this.inv.findByUid(it.uid);
    this.drag = { it, ghost, fromSlot: from ? from.cont.slot : null,
      ox: source.offsetWidth / 2, oy: source.offsetHeight / 2 };
    this._dragMove(e);
  }

  _dragMove(e) {
    if (!this.drag) return;
    this.drag.ghost.style.left = e.clientX - this.drag.ox + 'px';
    this.drag.ghost.style.top = e.clientY - this.drag.oy + 'px';
  }

  _dragEnd(e) {
    if (!this.drag) return;
    const { it, ghost, fromSlot } = this.drag;
    ghost.remove();
    this.drag = null;
    // find what we dropped on (the ghost is pointer-events:none)
    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (!target?.closest) return;
    // the vicinity column is the ground: a drop there is a DROP
    if (target.closest('[data-vicinity]')) {
      if (fromSlot == null) return; // already on the ground
      this.h.drop?.(it);
      return;
    }
    const cell = target.closest('.cell, .item');
    if (!cell) return; // dropped on the scrim or a panel border: no-op, no noise
    const gridEl = cell.closest('.grid');
    if (!gridEl) return;
    const slot = gridEl.dataset.slot;
    const cont = this.inv.containers().find(c => c.slot === slot);
    if (!cont) return;
    const CS = this._cs();
    const r = gridEl.getBoundingClientRect();
    const def = ITEMS[it.id];
    // center-anchor the footprint on the cursor, clamp inside the grid
    let x = Math.round((e.clientX - r.left) / CS - def.size[0] / 2);
    let y = Math.round((e.clientY - r.top) / CS - def.size[1] / 2);
    x = Math.max(0, Math.min(cont.grid[0] - def.size[0], x));
    y = Math.max(0, Math.min(cont.grid[1] - def.size[1], y));
    this._applyPlan(planDrop({
      item: it, fromSlot, to: cont, x, y, sizeOf: SIZE_OF, stackOf: STACK_OF,
    }), it, cont, gridEl);
  }

  // Carry out what invmove.js decided. Everything here goes through the
  // model's own API (move / removeItem / onChange) or through item-instance
  // fields the view already owns — setCondition() writes `cond` the same way —
  // because src/game/inventory.js belongs to another lane this round.
  _applyPlan(plan, it, cont, gridEl) {
    switch (plan.kind) {
      case 'none':
        return;
      case 'move':
        // move() still gets the last word (it owns the container arrays); a
        // false here means the two spatial models disagree, which is a bug
        // worth seeing rather than swallowing
        if (!this.inv.move(it, cont, plan.x, plan.y)) this._deny('It does not fit there.', gridEl);
        return;
      case 'merge': {
        const tgt = plan.target;
        tgt.qty = (tgt.qty || 1) + plan.qty;
        // a merged stack can never be in better condition than its worst part
        if (typeof tgt.cond === 'number' || typeof it.cond === 'number') {
          tgt.cond = mergedCond(tgt.cond, it.cond);
        }
        // removeItem splices the source when it empties, clears any quickslot
        // bound to it and fires onChange for both halves of the merge
        this.inv.removeItem(it, plan.qty);
        return;
      }
      case 'swap': {
        const o = plan.other;
        const ox = it.x, oy = it.y;
        it.x = o.x; it.y = o.y;
        o.x = ox; o.y = oy;
        this.inv.onChange?.();
        return;
      }
      default:
        this._deny(plan.reason === 'nospace'
          ? 'Nothing to swap with — the other item has nowhere to go.'
          : plan.reason === 'bounds' ? 'That is off the grid.'
            : 'That space is taken.', gridEl);
    }
  }

  // Say no out loud. A refused drop used to be indistinguishable from a broken
  // drag handler: both snapped the item home in silence.
  _deny(msg, gridEl) {
    this.deny.textContent = msg;
    this.deny.style.display = 'block';
    clearTimeout(this._denyT);
    this._denyT = setTimeout(() => { this.deny.style.display = 'none'; }, 1600);
    if (gridEl) {
      gridEl.classList.remove('deny');
      void gridEl.offsetWidth; // restart the flash even on a repeated refusal
      gridEl.classList.add('deny');
    }
  }

  // Split a stack in half. Prefers a model-side splitStack() the moment one
  // exists (see handoff notes); until then the half is minted here, out of a
  // uid range the model's counter can never reach. Returns false when there is
  // no free footprint for the half.
  _split(it) {
    const plan = planSplit(it, STACK_OF);
    if (!plan) return false;
    if (typeof this.inv.splitStack === 'function') return !!this.inv.splitStack(it, plan.take);
    const found = this.inv.findByUid(it.uid);
    if (!found) return false;
    const { cont } = found;
    const [gw, gh] = cont.grid;
    const [w, h] = SIZE_OF(it.id);
    let spot = null;
    for (let y = 0; y <= gh - h && !spot; y++) {
      for (let x = 0; x <= gw - w && !spot; x++) {
        if (fitsAt(cont.items, gw, gh, { x, y, w, h }, null, SIZE_OF)) spot = [x, y];
      }
    }
    if (!spot) return false;
    const half = { uid: ++viewUid, id: it.id, x: spot[0], y: spot[1], qty: plan.take };
    if (typeof it.cond === 'number') half.cond = it.cond;
    cont.items.push(half);
    it.qty = plan.keep;
    this.inv.onChange?.();
    return true;
  }

  // ---------- context menu ----------
  _openMenu(e, it) {
    const def = ITEMS[it.id];
    const rows = [];
    if (def.patch) rows.push(['Patch wound', () => this.h.patch(it, def)]);
    if (def.eat && !def.patch) {
      const verb = def.kind === 'drink' ? 'Drink' : def.kind === 'med' ? 'Use' : 'Eat';
      rows.push([verb, () => this.h.eat(it, def)]);
    }
    if (def.cookedId && this.h.canCook?.()) rows.push(['Roast over the fire', () => this.h.cook(it, def)]);
    if (it.id === 'matches') rows.push(['Light fire', () => this.h.lightFire?.()]);
    const sp = planSplit(it, STACK_OF);
    if (sp) {
      rows.push([`Split stack (${sp.take})`,
        () => { if (!this._split(it)) this._deny('No free space for the other half.'); }]);
    }
    if (def.kind === 'backpack' || def.kind === 'shirt' || def.kind === 'jacket' || def.kind === 'pants') {
      rows.push(['Equip', () => this.h.equip(it)]);
    }
    if (def.eat || def.patch || def.kind === 'tool') {
      for (let s = 0; s < this.quickslotCount(); s++) {
        rows.push([`Quickbar [${QUICK_KEYS[s]}]`,
          () => { this.inv.quickbar[s] = it.uid; this.renderQuickbar(); }]);
      }
    }
    rows.push(['Drop', () => this.h.drop(it)]);
    this.menu.innerHTML = '';
    for (const [label, fn] of rows) {
      const row = el('div', null, label);
      row.addEventListener('mousedown', (ev) => {
        ev.stopPropagation();
        this.menu.style.display = 'none';
        fn();
      });
      this.menu.appendChild(row);
    }
    this.menu.style.display = 'block';
    this.menu.style.left = Math.min(e.clientX, innerWidth - 170) + 'px';
    this.menu.style.top = Math.min(e.clientY, innerHeight - rows.length * 30 - 10) + 'px';
  }

  // ---------- always-on HUD ----------
  renderQuickbar() {
    const n = this.quickslotCount();
    this.qb.innerHTML = '';
    this._qslots = [];
    for (let s = 0; s < n; s++) {
      const uid = this.inv.quickbar[s];
      const found = uid && this.inv.findByUid(uid);
      const slot = el('div', 'qslot' + (found ? '' : ' empty'));
      this._qslots.push(slot);
      if (found) {
        const ic = iconFor(found.it.id);
        slot.appendChild(icon(ic.sym, 'ico', ic.tint));
        const c = condOf(found.it);
        // same Pristine gate as the grid: the quickbar must never paint a
        // 100%-white bar across a slot in an otherwise dark night frame
        const cbar = condBarEl(c);
        if (cbar) slot.appendChild(cbar);
        const qdef = ITEMS[found.it.id];
        this._tip(slot, { name: qdef.name, qty: found.it.qty, cond: c, size: qdef.size,
          wt: massOfStack(found.it), desc: qdef.desc });
        if (found.it.qty > 1) slot.appendChild(el('span', 'qqty tnum', String(found.it.qty)));
      }
      slot.appendChild(el('span', 'key', QUICK_KEYS[s]));
      this.qb.appendChild(slot);
    }
  }

  // the slot the player just triggered stays lit for a beat — DayZ's quickbar
  // has no persistent selection, so "active" is a use pulse, not a cursor
  flashQuickslot(s) {
    pokeHud(); // using a slot is the clearest "show me the hotbar" there is
    const slot = this._qslots?.[s];
    if (!slot) return;
    slot.classList.add('on');
    clearTimeout(this._qbTimer);
    this._qbTimer = setTimeout(() => slot.classList.remove('on'), 900);
  }

  useQuickslot(s) {
    if (s >= this.quickslotCount()) {
      // Every one of the 70 persisted players derives exactly two slots, so
      // keys 0 and - are dead for all of them — and this used to return before
      // flashQuickslot(), i.e. with the bar now VISIBLE the keys read as broken
      // rather than as absent. stats owns the toast channel (it is the only
      // route to it from here); all three hops are optional so a gym/test
      // construction without a HUD is unaffected.
      this.stats?.hud?.flashMove?.(`Quickslot ${QUICK_KEYS[s] || s + 1} needs better clothing`);
      return;
    }
    this.flashQuickslot(s);
    const uid = this.inv.quickbar[s];
    const found = uid && this.inv.findByUid(uid);
    if (!found) return;
    const def = ITEMS[found.it.id];
    if (def.patch) this.h.patch(found.it, def);
    else if (def.eat) this.h.eat(found.it, def);
  }

  // DayZ iconized vitals: colour encodes the value, blink only when critical,
  // and A GLYPH IS ONLY ON SCREEN WHEN IT HAS SOMETHING TO SAY. Column is flex
  // column-reverse, so the first slot sits at the bottom of the stack.
  //
  // The elements are built once and then mutated. Round 5 rewrote the column's
  // innerHTML four times a second, which (a) made a CSS fade impossible — every
  // glyph was a brand-new node — and (b) re-parsed five SVGs per update for a
  // number that changes once a minute.
  updateVitals() {
    const s = this.stats;
    // [key, symbol, value, threshold] — below the threshold the vital speaks
    const rows = [
      ['health', 'vi-heart', s.health, 95],
      ['blood', 'vi-blood', s.blood, 95],
      ['hunger', 'vi-food', s.hunger, 85],
      ['thirst', 'vi-drop', s.thirst, 85],
    ];
    if (!this._vslots) {
      this._vslots = {};
      const mk = (key, sym) => {
        const slot = el('div', 'vslot');
        const g = document.createElementNS(NS, 'svg');
        g.setAttribute('class', 'vic');
        const u = document.createElementNS(NS, 'use');
        u.setAttribute('href', '#' + sym);
        g.appendChild(u);
        slot.appendChild(g);
        if (key !== 'wound') slot.appendChild(el('i', 'vv'));
        this.vitals.appendChild(slot);
        this._vslots[key] = slot;
      };
      for (const [key, sym] of rows) mk(key, sym);
      mk('wound', 'vi-bandage');
      this._vslots.wound.querySelector('.vic').classList.add('blink');
      this._vslots.wound.querySelector('.vic').style.color = 'var(--hud-crit)';
    }
    let crit = s.wounds > 0;
    for (const [key, , raw, thr] of rows) {
      const val = Math.max(0, Math.min(100, Math.round(raw ?? 100)));
      const slot = this._vslots[key];
      const on = val < thr;
      slot.classList.toggle('on', on);
      if (!on) continue;
      const col = vitalColor(val);
      const g = slot.firstChild;
      g.style.color = col;
      g.classList.toggle('blink', val <= 15);
      slot.lastChild.style.background =
        `linear-gradient(90deg,${col} ${val}%,rgba(255,255,255,.10) ${val}%)`;
      if (val <= 15) crit = true;
    }
    this._vslots.wound.classList.toggle('on', s.wounds > 0);
    // a critical vital or an open wound overrides the idle attenuation
    this.vitals.classList.toggle('crit', crit);
  }

  // Called EVERY FRAME from the render loop (main.js:945), so it rebuilds
  // nothing unless the prompt actually changed: the old version re-parsed the
  // keycap markup and allocated a text node 60 times a second for a line that
  // is identical from frame to frame while the player stands in a doorway.
  setPrompt(text) {
    if (text === this._promptText) return;
    this._promptText = text;
    if (text) {
      this.prompt.innerHTML = '<span class="keycap">F</span> ';
      this.prompt.appendChild(document.createTextNode(text));
      this.prompt.style.display = 'block';
    } else {
      this.prompt.style.display = 'none';
    }
  }
}
